/**
 * Plomberie commune aux calculs déportés hors du fil principal.
 *
 * Deux calculs la partagent — les altitudes et l'analyse GPX —, et ils avaient
 * commencé par en avoir chacun une copie. Ce qui compte ici n'est pas la
 * longueur du code mais son unique règle de repli : **un travailleur qui
 * manque, refuse de naître ou meurt en route ne doit pas faire disparaître la
 * fonction**. Mieux vaut une seconde de gel qu'un import sans effet. Deux
 * copies de cette règle, c'est la garantie qu'une seule sera corrigée le jour
 * où elle se révélera fausse.
 */

/**
 * Confie une requête à un travailleur, avec repli sur le fil principal.
 *
 * Le travailleur est créé par appel plutôt que maintenu en vie : ces calculs
 * sont rares — un import, un routage, une récupération d'altitude — et un fil
 * dormant coûterait de la mémoire pour rien tout au long de la session.
 *
 * @param spawn Création du travailleur. Elle appartient à l'appelant parce que
 *   Vite ne reconnaît la forme `new Worker(new URL('./x.entry.ts',
 *   import.meta.url))` que littéralement, à l'endroit où elle est écrite : une
 *   URL passée en argument ne produirait aucun paquet.
 * @param onMainThread Le même calcul, sur place, pour le repli.
 */
export async function runInWorker<Req, Res>(
  spawn: () => Worker,
  request: Req,
  onMainThread: (request: Req) => Res,
): Promise<Res> {
  if (typeof Worker === 'undefined') return onMainThread(request)

  let worker: Worker
  try {
    worker = spawn()
  } catch {
    return onMainThread(request)
  }

  try {
    return await new Promise<Res>((resolve, reject) => {
      worker.addEventListener('message', (event: MessageEvent<Res>) => {
        resolve(event.data)
      })
      worker.addEventListener('error', () => {
        reject(new Error('travailleur indisponible'))
      })
      // `messageerror` est le cas que le clone structuré fait échouer à la
      // **réception** : la réponse est partie, elle n'a pas pu être relue.
      // Sans cet écouteur, plus rien n'arrive jamais — ni `message`, ni
      // `error` — et la promesse reste pendante à vie : import figé, aucun
      // message. C'est précisément ce que le commentaire de `gpxWorker.ts`
      // annonce comme échouant « pas à la compilation ».
      worker.addEventListener('messageerror', () => {
        reject(new Error('réponse du travailleur illisible'))
      })
      worker.postMessage(request)
    })
  } catch {
    return onMainThread(request)
  } finally {
    worker.terminate()
  }
}
